import type {
  IHostVerificationRow,
  ISoftwareInventoryRow,
} from "@/features/servers/schemas/servers";
import { OS_SUPPORT_STATUS_LABELS } from "@/features/servers/schemas/servers";
import { isPastRevisionWindow } from "@/features/servers/services/infraSettingsService";
import { formatOsLabel } from "@/features/servers/utils/os-support";
import { isVersionOutdated } from "@/features/servers/utils/semver";

export function daysSince(isoDate?: string): number | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportSoftwareCsv(
  rows: ISoftwareInventoryRow[],
  intervalMonths: number
) {
  const header = [
    "Centro",
    "Email",
    "Software",
    "Versión instalada",
    "Versión objetivo",
    "Desactualizado",
    "Verificado el",
    "Verificado por",
    "Pendiente verificación",
    "Último update",
    "Activo",
    "Host",
    "IP host",
    "URL",
    "UID centro",
  ];
  const lines = rows.map((r) => {
    const pendingVerify = isPastRevisionWindow(r.verifiedAt, intervalMonths);
    const outdated = isVersionOutdated(r.version, r.targetVersion);
    return [
      r.institutionName,
      r.institutionEmail ?? "",
      r.softwareLabel,
      r.version ?? "",
      r.targetVersion ?? "",
      outdated ? "sí" : "no",
      r.verifiedAt ?? "",
      r.verifiedBy ?? "",
      pendingVerify ? "sí" : "no",
      r.lastUpdatedAt ?? "",
      r.active ? "sí" : "no",
      r.hostName ?? "",
      r.hostIp ?? "",
      r.url ?? "",
      r.institutionUid,
    ]
      .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
      .join(",");
  });
  downloadCsv(
    `inventario-software-${new Date().toISOString().slice(0, 10)}.csv`,
    [header.join(","), ...lines].join("\n")
  );
}

export function exportHostsCsv(
  rows: IHostVerificationRow[],
  intervalMonths: number
) {
  const header = [
    "Centro",
    "Email centro",
    "Host",
    "SO",
    "Familia SO",
    "Versión SO",
    "Estado soporte",
    "Recibe updates software",
    "SO recomendado",
    "SO mínimo",
    "IP",
    "AnyDesk",
    "Verificado el",
    "Verificado por",
    "Pendiente revisión",
    "UID centro",
  ];
  const lines = rows.map((r) => {
    const pending = isPastRevisionWindow(r.verifiedAt, intervalMonths);
    const osLabel = formatOsLabel(r.osFamily, r.osVersion, r.os) || r.os || "";
    return [
      r.institutionName,
      r.institutionEmail ?? "",
      r.hostName,
      osLabel,
      r.osFamily ?? "",
      r.osVersion ?? "",
      OS_SUPPORT_STATUS_LABELS[r.osSupportStatus],
      r.acceptsSoftwareUpdates ? "sí" : "no",
      r.recommendedOsVersion ?? "",
      r.minSupportedOsVersion ?? "",
      r.hostIp ?? "",
      r.anydeskId ?? "",
      r.verifiedAt ?? "",
      r.verifiedBy ?? "",
      pending ? "sí" : "no",
      r.institutionUid,
    ]
      .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
      .join(",");
  });
  downloadCsv(
    `soporte-so-hosts-${new Date().toISOString().slice(0, 10)}.csv`,
    [header.join(","), ...lines].join("\n")
  );
}
